import { useEffect, useCallback } from "react";

interface UseFlyerItemKeyboardNavigationProps {
  isOpen: boolean;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  canNavigatePrevious: boolean;
  canNavigateNext: boolean;
}

/**
 * Custom hook for keyboard navigation between flyer items in the modal.
 * Supports Ctrl/Cmd + ArrowLeft/Right for Windows, Linux, and macOS.
 *
 * @param isOpen - Whether the modal is currently open
 * @param onNavigatePrevious - Callback to navigate to previous flyer item
 * @param onNavigateNext - Callback to navigate to next flyer item
 * @param canNavigatePrevious - Whether navigation to previous item is possible
 * @param canNavigateNext - Whether navigation to next item is possible
 */
export const useFlyerItemKeyboardNavigation = ({
  isOpen,
  onNavigatePrevious,
  onNavigateNext,
  canNavigatePrevious,
  canNavigateNext,
}: UseFlyerItemKeyboardNavigationProps) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle keyboard events when modal is open
      if (!isOpen) {
        return;
      }

      // Prevent navigation when user is typing in an input, textarea, or contenteditable
      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isInputElement) {
        return;
      }

      // Check for Ctrl (Windows/Linux) or Cmd (macOS) modifier
      const isModifierPressed = event.ctrlKey || event.metaKey;

      if (!isModifierPressed) {
        return;
      }

      if (event.key === "ArrowLeft") {
        if (canNavigatePrevious) {
          event.preventDefault();
          event.stopPropagation();
          onNavigatePrevious();
        }
        return;
      }

      if (event.key === "ArrowRight") {
        if (canNavigateNext) {
          event.preventDefault();
          event.stopPropagation();
          onNavigateNext();
        }
        return;
      }
    },
    [
      isOpen,
      onNavigatePrevious,
      onNavigateNext,
      canNavigatePrevious,
      canNavigateNext,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  return null;
};
